import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { crmApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, TrendingUp, DollarSign, Target, ArrowRight,
  Loader2, Database, PieChart, BarChart3, Zap, UserPlus
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const STAGE_COLORS = {
  new: '#1A56DB', contacted: '#0E7490', qualified: '#7C3AED',
  proposal: '#92400E', negotiation: '#DC2626', closed_won: '#166534', closed_lost: '#64748B'
};
const STAGE_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  proposal: 'Proposal', negotiation: 'Negotiation', closed_won: 'Won', closed_lost: 'Lost'
};
const PIE_COLORS = ['#0E7490', '#1A56DB', '#7C3AED', '#92400E', '#166534'];

export default function CRMDashboard() {
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, pipeRes] = await Promise.all([crmApi.dashboard(), crmApi.pipeline()]);
      setData(dashRes.data);
      setPipeline(pipeRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await crmApi.seedDemo();
      toast.success('CRM demo data seeded');
      load();
    } catch (err) {
      toast.error('Failed to seed');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>CRM Dashboard</h1>
          <p className="text-[#64748B] mt-1">Sales pipeline, leads, and marketing overview</p>
        </div>
        <div className="flex gap-2">
          {s.total === 0 && (
            <Button onClick={seedDemo} disabled={seeding} variant="outline" data-testid="seed-crm-btn">
              <Database className="w-4 h-4 mr-2" />{seeding ? 'Seeding...' : 'Seed Demo Data'}
            </Button>
          )}
          <Link to="/crm/leads"><Button className="careiq-btn-primary" data-testid="view-leads-btn"><UserPlus className="w-4 h-4 mr-2" />Manage Leads</Button></Link>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="crm-kpis">
        {[
          { label: 'Total Leads', value: s.total || 0, icon: Users, color: '#0E7490' },
          { label: 'Active Pipeline', value: s.active || 0, icon: Target, color: '#1A56DB' },
          { label: 'Won Deals', value: s.won || 0, icon: TrendingUp, color: '#166534' },
          { label: 'Conversion Rate', value: `${s.conversion_rate || 0}%`, icon: Zap, color: '#7C3AED' },
          { label: 'Won Revenue', value: `$${(s.won_value || 0).toLocaleString()}`, icon: DollarSign, color: '#166534' },
        ].map((kpi, i) => (
          <Card key={i} className="careiq-metric-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.color + '15' }}>
                  <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#0A2342]">{kpi.value}</div>
                  <div className="text-xs text-[#64748B]">{kpi.label}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pipeline Visual */}
      {pipeline && (
        <Card className="careiq-card" data-testid="crm-pipeline">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#0E7490]" />
              Sales Pipeline (${(pipeline.pipeline_value || 0).toLocaleString()} in pipeline)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'].map((stage) => {
                const stg = pipeline.pipeline?.[stage] || { count: 0, total_value: 0, leads: [] };
                return (
                  <div key={stage} className="flex-1 min-w-[140px] p-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC]" data-testid={`pipeline-stage-${stage}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                      <span className="text-xs font-semibold" style={{ color: STAGE_COLORS[stage] }}>{STAGE_LABELS[stage]}</span>
                    </div>
                    <div className="text-2xl font-bold text-[#0A2342]">{stg.count}</div>
                    <div className="text-[10px] text-[#64748B]">${(stg.total_value || 0).toLocaleString()}</div>
                    <div className="mt-2 space-y-1">
                      {stg.leads?.slice(0, 3).map((l, i) => (
                        <div key={i} className="text-[10px] p-1.5 rounded bg-white border border-[#E2E8F0] truncate">
                          <span className="font-medium text-[#0A2342]">{l.name}</span>
                          {l.company && <span className="text-[#64748B]"> &middot; {l.company}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* By Source */}
        <Card className="careiq-card" data-testid="leads-by-source">
          <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342]">Leads by Source</CardTitle></CardHeader>
          <CardContent>
            {(data?.by_source || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.by_source}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="source" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0E7490" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center text-[#64748B] py-8">No data yet</p>}
          </CardContent>
        </Card>

        {/* By Type */}
        <Card className="careiq-card" data-testid="leads-by-type">
          <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342]">Leads by Type</CardTitle></CardHeader>
          <CardContent>
            {(data?.by_type || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie data={data.by_type.map(t => ({ name: t.type === 'b2b' ? 'B2B' : t.type === 'patient_referral' ? 'Patient Referral' : 'Partner', value: t.count }))} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {data.by_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            ) : <p className="text-center text-[#64748B] py-8">No data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Recent Leads */}
      <Card className="careiq-card" data-testid="recent-leads">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Recent Leads</CardTitle>
          <Link to="/crm/leads"><Button variant="ghost" size="sm" className="text-[#0E7490]">View All <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(data?.recent_leads || []).map((l, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ backgroundColor: STAGE_COLORS[l.stage] || '#64748B' }}>
                  {l.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm text-[#0A2342]">{l.name}</span>
                  {l.company && <span className="text-xs text-[#64748B]"> &middot; {l.company}</span>}
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (STAGE_COLORS[l.stage] || '#64748B') + '15', color: STAGE_COLORS[l.stage] || '#64748B' }}>
                  {STAGE_LABELS[l.stage] || l.stage}
                </span>
                <span className="text-sm font-medium text-[#0A2342]">${(l.value || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/crm/leads"><Card className="careiq-card hover:border-[#0E7490] transition-colors cursor-pointer"><CardContent className="p-4 text-center"><Users className="w-6 h-6 mx-auto text-[#0E7490] mb-2" /><div className="text-sm font-medium text-[#0A2342]">Leads</div></CardContent></Card></Link>
        <Link to="/crm/campaigns"><Card className="careiq-card hover:border-[#0E7490] transition-colors cursor-pointer"><CardContent className="p-4 text-center"><Zap className="w-6 h-6 mx-auto text-[#7C3AED] mb-2" /><div className="text-sm font-medium text-[#0A2342]">Campaigns</div></CardContent></Card></Link>
        <Link to="/crm/templates"><Card className="careiq-card hover:border-[#0E7490] transition-colors cursor-pointer"><CardContent className="p-4 text-center"><PieChart className="w-6 h-6 mx-auto text-[#92400E] mb-2" /><div className="text-sm font-medium text-[#0A2342]">Templates</div></CardContent></Card></Link>
        <Link to="/crm/leads"><Card className="careiq-card hover:border-[#0E7490] transition-colors cursor-pointer"><CardContent className="p-4 text-center"><Target className="w-6 h-6 mx-auto text-[#166534] mb-2" /><div className="text-sm font-medium text-[#0A2342]">Pipeline</div></CardContent></Card></Link>
      </div>
    </div>
  );
}
